import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ILegalDocument } from 'api/digifi/dlp/SettingsApi';
import { BorrowerType } from 'enums/BorrowerType';
import DigitalLendingPortalEnvironment from 'enums/asd';
import { environmentApi, settingsApi } from 'store/api';


enum SettingsActionType {
  GetSettings = 'settings/getSettings',
  GetEnvironment = 'settings/getEnvironment',
  GetBranding = 'settings/getBranding',
}

export const DEFAULT_PHONE_NUMBER_FORMAT = 'US';
const DEFAULT_CURRENCY = '$';

interface IBrandingParams {
  accentColor: string | null;
  brandColor: string | null;
  faviconId: string | null;
  logoId: string | null;
  companyName: string | null;
  country: string | null;
}

interface IPortalConfiguration {
  borrowerType: BorrowerType;
}

interface IVariablesSettings {
  phoneNumberFormat: string;
  currency: string;
}

export interface ISettingsState {
  branding: IBrandingParams;
  portalConfiguration: IPortalConfiguration;
  variablesSettings: IVariablesSettings;
  legalDocuments: ILegalDocument[];
  selectedDocument: ILegalDocument | null;
  environment: DigitalLendingPortalEnvironment | null;
  progress: number | null;
}

const initialState: ISettingsState = {
  branding: {
    accentColor: null,
    brandColor: null,
    faviconId: null,
    logoId: null,
    companyName: null,
    country: null,
  },
  portalConfiguration: {
    borrowerType: BorrowerType.Person,
  },
  variablesSettings: {
    phoneNumberFormat: DEFAULT_PHONE_NUMBER_FORMAT,
    currency: DEFAULT_CURRENCY,
  },
  legalDocuments: [],
  selectedDocument: null,
  environment: null,
  progress: null,
};

export const getEnvironment = createAsyncThunk(SettingsActionType.GetEnvironment, async () => {
  return environmentApi.getEnvironment();
});

export const getSettings = createAsyncThunk(SettingsActionType.GetSettings, async () => {
  const branding = await settingsApi.getBranding();
  const legalDocuments = await settingsApi.getLegalDocuments();
  const portalConfiguration = await settingsApi.getPortalConfiguration();
  const variablesSettings = await settingsApi.getVariablesSettings();

  return { branding, legalDocuments, portalConfiguration, variablesSettings };
});

export const getBranding = createAsyncThunk(SettingsActionType.GetBranding, async () => {
  const branding = await settingsApi.getBranding();

  return { branding };
});

const settingsSlice = createSlice({
  name: 'settingsSlice',
  initialState,
  reducers: {
    setSelectedDocument(state, { payload }: PayloadAction<ILegalDocument | null>) {
      state.selectedDocument = payload;
    },
    setProgress(state, { payload }: PayloadAction<number | null>) {
      state.progress = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSettings.fulfilled, (state, { payload }) => {
        state.branding = payload.branding || initialState.branding;
        state.portalConfiguration = payload.portalConfiguration || initialState.portalConfiguration;
        state.variablesSettings = payload.variablesSettings || initialState.variablesSettings;
        state.legalDocuments = payload.legalDocuments || initialState.legalDocuments;
      })
      .addCase(getEnvironment.fulfilled, (state, { payload }) => {
        state.environment = payload;
      })
      .addCase(getBranding.fulfilled, (state, { payload }) => {
        state.branding = payload.branding || initialState.branding;
      });
  },
});

export const { setSelectedDocument, setProgress } = settingsSlice.actions;

export default settingsSlice.reducer;
